import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import spainmundo from "../assets/sponsors/spainmundo.png";
import agreaterstar from "../assets/sponsors/agreaterstar.png";
import mett from "../assets/sponsors/mett.png";
import theflag from "../assets/sponsors/theflag.png";
import spisa from "../assets/sponsors/spisa.png";
import middagskassen from "../assets/sponsors/middagskassen.png";

const title = "Sponsorer | Swedish International School Estepona";
const description =
  "Skolans sponsorer bidrar till extra upplevelser för eleverna. Intäkterna går oavkortat till eleverna eller till skolans familjeevent.";

export const Route = createFileRoute("/sponsorer")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/sponsorer") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/sponsorer"),
  }),
  component: Sponsorer,
});

const sponsors = [
  {
    name: "Spainmundo",
    logo: spainmundo,
    url: "https://www.spainmundo.com/",
    text: "Flytta med förtroende med Spainmundo – din partner för smidig och problemfri omlokalisering till Spanien. Vårt team av experter inom internationell flyttning tar hand om allt, från pappersarbete och boende till kulturell anpassning.",
  },
  {
    name: "A Greater Star",
    logo: agreaterstar,
    url: "https://greaterstar.io",
    text: "Upptäck den digitala världens oändliga möjligheter med A Greater Star, din pålitliga partner inom webbdesign och digital marknadsföring, med expertis inom webbutveckling, SEO och digital strategi.",
  },
  {
    name: "METT",
    logo: mett,
    text: "The 5-star lifestyle property METT Hotel & Beach Resort features 249 elegant and beautifully designed guest rooms and suites around Azure Beach’s heated pool with uninterrupted views of the Mediterranean Sea.",
  },
  {
    name: "The Flag",
    logo: theflag,
    text: "THE FLAG Costa del Sol in Estepona near Marbella invites you to unwind – whether you want to enjoy a relaxing dream holiday or take an active approach to your wellbeing with our wellbeing experts and holistic program.",
  },
  {
    name: "Spisa",
    logo: spisa,
    url: "http://www.spisa.shop/",
    text: "Spisa foodmarket Marbella, Estepona. Scandinavian groceries, deli, bakery, catering! Open 9 to 21, 365 days a year.",
  },
  {
    name: "Middagskassen.se",
    logo: middagskassen,
    url: "https://www.middagskassen.se/",
    text: "Middagskassen.se jämför Sveriges största och mest populära leverantörer av matkassar med hemleverans och hjälper dig hitta den perfekta lösningen för en stressfri vardag.",
  },
];

function Sponsorer() {
  return (
    <>
      <PageHero
        eyebrow="Samarbeten"
        title="Sponsorer"
        intro="Skolan har samarbete med företag som bidrar till att eleverna får möjlighet till extra fina upplevelser. Alla intäkter från sponsorer går oavkortat till eleverna eller till någon av våra familjeevent."
      />

      <Prose>
        <p>
          Tack vare hjälp eller bidrag från sponsorer kan skolan erbjuda bland annat tekniska
          hjälpmedel eller annat som bidrar till elevernas skolgång och vardag. Skolan är en naturlig
          mötesplats och flera gånger per år har alla familjer möjlighet att träffas även utanför
          skoltid – som sponsor kan du vara med och bidra.
        </p>
        <p>
          Vill ditt företag vara med som sponsor? Hör av dig på{" "}
          <a href="mailto:info@sweintschool.com">info@sweintschool.com</a>.
        </p>

        <h2>Vad sponsringen används till</h2>
        <p>
          Intäkter från sponsorer går till elevernas skolgång och till skolans gemensamma tillfällen –
          det kan handla om material, utrustning eller kostnader kring familjeevenemang. Skolan är en
          liten verksamhet, och den här typen av stöd gör skillnad i vardagen.
        </p>

        <h2>Att synas som partner</h2>
        <p>
          Som sponsor blir företaget en del av ett sammanhang med familjer från flera länder som bor
          och verkar mellan Estepona och Marbella. Upplägget ser olika ut beroende på vad ni vill bidra
          med – ekonomiskt stöd, produkter, tjänster eller att vara med vid ett arrangemang. Vi
          diskuterar gärna vad som passar ert företag.
        </p>


      </Prose>

      <div className="mx-auto grid max-w-5xl gap-6 px-5 pb-16 sm:grid-cols-2">
        {sponsors.map((s) => (
          <article key={s.name} className="rounded-xl border border-border bg-card p-7 shadow-soft">
            <div className="flex h-20 items-center">
              <img
                src={s.logo}
                alt={`${s.name} logotyp`}
                loading="lazy"
                className="max-h-16 w-auto max-w-[220px] object-contain"
              />
            </div>
            <h2 className="mt-5 text-lg font-semibold">{s.name}</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
            {s.url && (
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block text-sm text-primary link-quiet"
              >
                Besök webbplatsen
              </a>
            )}
          </article>
        ))}
      </div>

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
